import Helpers from 'tools/helpers';

/**
 * PROGRESS: A Creative Agency - Standard lazy load script
 *
 * @author Caelan Stewart <caelan@progress.agency>
 */

let elements,
    imagesByReference,
    windowHeight,
    windowWidth,
    bodyRect;

/**
 * Returns the offset of an element relative to the top of the page.
 *
 * @param {HTMLElement} element
 * @returns {Number}
 */
function getPositionY(element) {
    let elemRect = element.getBoundingClientRect();

    return elemRect.top - bodyRect.top;
}

/**
 * Caches image elements, and sorts them into an object by reference.
 *
 * @returns {undefined}
 */
function cacheElements() {
    elements = {};
    imagesByReference = {};

    elements.lazyLoadImages = document.querySelectorAll('.lazy-load:not(.lazy-loaded)');

    let lazyLoadImagesCount = elements.lazyLoadImages.length,
        lazyLoadImages = elements.lazyLoadImages,
        iter;

    for (iter = 0; iter < lazyLoadImagesCount; ++iter) {
        let lazyLoadImage = lazyLoadImages[iter],
            reference = lazyLoadImage.getAttribute('data-reference');

        // In case of malformed reference, show image anyway and log error.
        if ( ! reference) {
            reference = 'on-load';
        }

        // Check to see if an array for given reference exists, if not, add it.
        if (typeof imagesByReference[reference] !== 'object') {
            imagesByReference[reference] = [];
        }

        imagesByReference[reference].push(lazyLoadImage);
    }

    cacheElementOffsets();
}

/**
 * WebKit browsers force a DOM reflow whenever you get the offset, width or
 * height of an element/window.
 *
 * This function caches the offsetTop of 'on-scroll' lazy-loaded images under
 * a properted called 'cachedOffsetTop' to prevent the above issue.
 *
 * @returns {undefined}
 */
function cacheElementOffsets() {
    bodyRect = document.body.getBoundingClientRect();

    if ('on-scroll' in imagesByReference) {
        let images = imagesByReference['on-scroll'],
            count = images.length,
            iter;

        for (iter = 0; iter < count; ++iter) {
            images[iter].cachedOffsetTop = getPositionY(images[iter]);
        }
    }
}

function closest(number, array) {
    let current,
        low = 0,
        high = array.length - 1;

    while (high - low > 1) {
        current = ~~((low + high) / 2);

        if (array[current] < number) {
            low = current;
        } else {
            high = current;
        }
    }

    if (number - array[low] <= array[high] - number) {
        return array[low];
    }

    return array[high];
}

/**
 * Loads a specific image by element directly, no reference.
 *
 * @param {HTMLImageElement} image
 * @returns {undefined}
 */
function loadImage(image) {
    if (image.classList.contains('lazy-loaded')) {
        return;
    }

    let sizes = [
            ['xs', 0],
            ['sm', 568],
            ['md', 768],
            ['lg', 992],
            ['xl', 1200]
        ],
        src,
        iter,
        sizesLen = sizes.length,
        windowWidth = document.body.offsetWidth,
        availableSizesSrc = {},
        availableSizes = [],
        nearestSize;

    for (iter = 0; iter < sizesLen; ++iter) {
        let size = sizes[iter];

        if (image.hasAttribute('data-src-' + size[0])) {
            availableSizesSrc[size[1]] = image.getAttribute('data-src-' + size[0]);
            availableSizes.push(size[1]);
        }
    }

    if (availableSizes.length) {
        nearestSize = closest(windowWidth, availableSizes);

        src = availableSizesSrc[nearestSize];
    } else {
        src = image.getAttribute('data-src');
    }

    if (!src) {
        console.log('Error: Image is missing data-src/data-src-* attribute. Cannot lazy load.', image);
    }

    image.src = src;

    image.classList.add('lazy-loaded');
}

/**
 * Loads images that have the given reference.
 *
 * @param {string} reference - The reference of the images that should be loaded.
 * @returns {undefined}
 */
function load(reference) {
    // Verify validity of given reference
    if (typeof reference !== 'string' || reference.length === 0) {
        throw new Error('Invalid reference');
    }

    // Checks to see if the given reference exists
    if (typeof imagesByReference[reference] !== 'object') {
        return;
    }

    let images = imagesByReference[reference],
        imagesLen = images.length,
        iter;

    // Iterate images and load them 
    for (iter = 0; iter < imagesLen; ++iter) {
        loadImage(images[iter]);
    }

    // The references to the images are no longer required, the images are
    // loaded. So get rid of them to free memory.
    delete imagesByReference[reference];
}

/**
 * Loads lazy-loaded images by a selector.
 *
 * @param {string} selector
 * @returns {undefined}
 */
function loadBySelector(selector) {
    let results = document.querySelectorAll(selector),
        resultsLen = results.length,
        iter;

    for (iter = 0; iter < resultsLen; ++iter) {
        let image = results[iter];

        if (image.classList.contains('lazy-load') && typeof image.src === 'string') {
            loadImage(image);
        }
    }
}

/**
 * Executes on window scroll to check if 'on-scroll' images are in view and
 * should be loaded.
 *
 * @returns {undefined}
 */
function onWindowScroll() {
    if (typeof imagesByReference['on-scroll'] !== 'object') return;

    let scrollTop = window.pageYOffset || document.documentElement.scrollTop,
        scrollTopThreshold = scrollTop + windowHeight + ~~(windowHeight * 0.5),
        images = imagesByReference['on-scroll'],
        imagesCount = images.length,
        iter;

    for (iter = 0; iter < imagesCount; ++iter) {
        let image = images[iter],
            offsetTop = image.cachedOffsetTop || image.offsetTop;

        if (typeof image.lazyLoaded !== 'undefined') {
            continue;
        }

        if (scrollTopThreshold > offsetTop) {
            loadImage(image);
        }
    }
}

/**
 * To be fired when a DOM change has been made, requiring certain values to be
 * re-cached and calculations to be recalculated.
 *
 * @returns {undefined}
 */
function domHasChanged() {
    cacheElements();
    cacheElementOffsets();
    onWindowScroll();

    load('page-load');
    load('on-load');
}

/**
 * Executes in window resize to cache the value of window.innerHeight.
 *
 * @returns {undefined}
 */
function onWindowResize() {
    windowHeight = window.innerHeight;
    windowWidth = document.body.offsetWidth;

    cacheElementOffsets();
    onWindowScroll();
}

/**
 * Executes on window load to do any work that needs to be done before images
 * begin getting loaded.
 *
 * @returns {undefined}
 */
function onWindowLoad() {
    windowHeight = window.innerHeight;
    windowWidth = document.body.offsetWidth;

    domHasChanged();

    setTimeout(() => {
        cacheElementOffsets();
        onWindowScroll();
    }, 250);
}

/**
 * Sets listeners that will only be set once.
 *
 * @returns {undefined}
 */
function setFixedListeners() {
    window.addEventListener('scroll', Helpers.throttledFunction(100, onWindowScroll));
    window.addEventListener('load', onWindowLoad);
    window.addEventListener('resize', Helpers.delayedFunction(500, onWindowResize));
}

function init() {
    cacheElements();
    setFixedListeners();
}

export default {
    cacheElements,
    load,
    domHasChanged,
    loadBySelector,
    init
};


// WEBPACK FOOTER //
// ./site/templates/resources/src/js/tools/lazy-load.js